import { useState } from "react";
import { Plus, Minus } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Reveal } from "@/components/Reveal";

/**
 * FAQ accordion. FAQPage structured data is emitted statically per route
 * at build time (scripts/prerender_seo.py) — never injected client-side,
 * so crawlers see exactly one route-owned representation.
 */
export default function Faq({ items = [], title = "Frequently asked questions", eyebrow = "Answers, straight up" }) {
  const [open, setOpen] = useState(0);

  if (!items.length) return null;

  return (
    <section data-testid="faq-section" className="max-w-[1400px] mx-auto px-5 sm:px-8 py-24 sm:py-28 relative z-10">
      <div className="grid lg:grid-cols-12 gap-10">
        <Reveal className="lg:col-span-5">
          <p className="overline text-[#8B0000] mb-4">{eyebrow}</p>
          <h2 className="font-serif text-5xl sm:text-6xl font-light tracking-tight text-[#143D63] leading-[1]">{title}</h2>
          <p className="text-[#143D63]/70 mt-6 max-w-md">
            Real answers to the questions Kolkata homeowners and business owners ask us every week — pricing, timelines, materials and warranties.
          </p>
        </Reveal>
        <div className="lg:col-span-7 space-y-3" data-testid="faq-list">
          {items.map((it, i) => {
            const active = open === i;
            return (
              <div key={i} className="border border-[#143D63]/15 bg-white">
                <button
                  data-testid={`faq-q-${i}`}
                  aria-expanded={active}
                  onClick={() => setOpen(active ? -1 : i)}
                  className="w-full text-left flex items-start justify-between gap-6 px-6 sm:px-7 py-6 group"
                >
                  <span className="font-serif text-xl text-[#143D63] leading-snug">{it.q}</span>
                  <span className={`shrink-0 mt-1 transition-colors ${active ? "text-[#8B0000]" : "text-[#143D63]/60 group-hover:text-[#143D63]"}`}>
                    {active ? <Minus size={20} /> : <Plus size={20} />}
                  </span>
                </button>
                <AnimatePresence initial={false}>
                  {active && (
                    <motion.div
                      key="a"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
                      className="overflow-hidden"
                    >
                      <p
                        data-testid={`faq-a-${i}`}
                        className="px-6 sm:px-7 pb-7 -mt-1 text-[15px] leading-relaxed text-[#143D63]/80 whitespace-pre-line"
                      >
                        {it.a}
                      </p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
